import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { InfoSectionContainer } from '@/common/InfoSection';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import {
  type TargetArea,
  TargetAreaMapCard,
} from '~/modules/web-ads-targets/TargetAreaMapCard';
import { GroupHeaderWrapper } from '~/styles/layoutElements';
import type { LanguageCode } from '~/types/congregationGroupTypes';
import { loadGoogleMaps } from '~/utils/loadGoogleMaps';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/web-ads-targets/',
)({
  component: WebTargetsRoute,
});

const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 30, lng: -20 };

// Coverage-circle colors: my group's targets vs. everyone else's footprints.
const MY_TARGET_COLOR = '#3d8ecd';
const OTHER_TARGET_COLOR = '#d21034';

type GeoTargetItem = components['schemas']['GeoTargetItem'];

// Friendly labels for the audience enum the API returns ("ALL" -> "All Students").
// Translation Needed — possible match: t('all-students') for ALL; POSTAL/ONLINE
// have no existing key yet.
const STUDENT_TYPE_LABELS: Record<string, string> = {
  ALL: 'All Students',
  POSTAL: 'Postal Students Only',
  ONLINE: 'Online Students Only',
};

// Map an API target onto the card's shape: meters -> miles, and the canonical
// language codes ("EN", "PT_BR") back to the UI's lowercase-dash form.
function toTargetArea(target: GeoTargetItem): TargetArea {
  return {
    id: target.id,
    locationName: target.title,
    center: { lat: target.lat, lng: target.lng },
    radiusMiles: target.radius ? Math.round(target.radius / 1609.34) : 0,
    languages: target.languages.map(
      (l) => l.toLowerCase().replace('_', '-') as LanguageCode,
    ),
    studentType:
      STUDENT_TYPE_LABELS[target.student_type] ?? target.student_type,
    active: target.is_active,
  };
}

function WebTargetsRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/web-ads-targets/',
  });
  const mapRef = useRef<HTMLDivElement>(null);

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id;

  // This group's own targets (the "My Target Areas" cards + blue circles).
  const { data: myTargets } = api.listGroupGeoTargets.useQuery(
    { path: { group_id: groupId ?? 0 } },
    { enabled: !!groupId },
  );
  // Every group's footprint (anonymized) for the map's context circles.
  const { data: mapTargets } = api.listGeoTargetMap.useQuery({});

  const targetAreas = (myTargets ?? []).map(toTargetArea);

  const handleCreateWebTarget = () => {
    navigate({
      to: '/groups/$groupName/web-ads-targets/add/',
      params: { groupName },
      search: { id: undefined },
    });
  };

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const mapEl = mapRef.current;
    if (!mapEl) return;

    let circles: google.maps.Circle[] = [];

    loadGoogleMaps(apiKey)
      .then(() => {
        if (!mapRef.current || !window.google?.maps) return;

        const map = new window.google.maps.Map(mapRef.current, {
          center: DEFAULT_CENTER,
          zoom: 2,
          mapTypeControl: false,
          streetViewControl: false,
          scaleControl: false,
          rotateControl: true,
          fullscreenControl: true,
          zoomControl: true,
        });

        const bounds = new window.google.maps.LatLngBounds();
        let hasCircle = false;

        const drawCircle = (
          center: google.maps.LatLngLiteral,
          radiusMeters: number,
          color: string,
        ) => {
          // Extend the map bounds by the circle's rough N/E/S/W extent so
          // fitBounds frames it — a real ~5-mile target is a sub-pixel dot at
          // the world zoom the map opens on.
          const latDelta = radiusMeters / 111_000;
          const lngDelta =
            radiusMeters / (111_000 * Math.cos((center.lat * Math.PI) / 180));
          bounds.extend({
            lat: center.lat + latDelta,
            lng: center.lng + lngDelta,
          });
          bounds.extend({
            lat: center.lat - latDelta,
            lng: center.lng - lngDelta,
          });
          hasCircle = true;
          return new window.google.maps.Circle({
            center,
            radius: radiusMeters,
            fillColor: color,
            fillOpacity: 0.4,
            strokeWeight: 0,
            clickable: false,
            map,
          });
        };

        // Only radius targets have a circle to draw (whole-region ones don't).
        circles = [
          // Everyone's footprints first (context)...
          ...(mapTargets ?? [])
            .filter((item) => item.radius)
            .map((item) =>
              drawCircle(
                { lat: item.lat, lng: item.lng },
                item.radius as number,
                OTHER_TARGET_COLOR,
              ),
            ),
          // ...then this group's targets highlighted on top.
          ...(myTargets ?? [])
            .filter((item) => item.radius)
            .map((item) =>
              drawCircle(
                { lat: item.lat, lng: item.lng },
                item.radius as number,
                MY_TARGET_COLOR,
              ),
            ),
        ];

        // Zoom to frame the targets so the circles are visible; keep the world
        // view when there are none.
        if (hasCircle) map.fitBounds(bounds);
      })
      .catch(() => {});

    return () => {
      for (const c of circles) c.setMap(null);
    };
  }, [mapTargets, myTargets]);

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('wbs.web-targets')} />
        <WBSButton wide onClick={handleCreateWebTarget}>
          {t('actions.create-new-web-target')}
        </WBSButton>
      </GroupHeaderWrapper>

      <MapCard>
        <Legend>
          <LegendItem>
            <Dot $color="var(--blue)" />
            <LegendLabel>{t('wbs.my-target-areas')}</LegendLabel>
          </LegendItem>
          <LegendItem>
            <Dot $color="var(--wbs-red)" />
            <LegendLabel>{t('groups.other-groups')}</LegendLabel>
          </LegendItem>
        </Legend>
        <OverviewMapContainer ref={mapRef} />

        <TargetAreaSection>
          <SectionHeader>{t('wbs.my-target-areas')}</SectionHeader>
          <TargetAreaGrid>
            {targetAreas.map((target) => (
              <TargetAreaMapCard
                key={target.id}
                target={target}
                onEdit={() =>
                  navigate({
                    to: '/groups/$groupName/web-ads-targets/add/',
                    params: { groupName },
                    search: { id: target.id },
                  })
                }
              />
            ))}
          </TargetAreaGrid>
        </TargetAreaSection>
      </MapCard>
    </>
  );
}

const MapCard = styled(InfoSectionContainer)`
  border-radius: 5px;
  padding-bottom: 120px;
`;

const Legend = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 18px;
`;

const LegendItem = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
`;

const Dot = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
  border-radius: 50%;
  flex-shrink: 0;
  height: 15px;
  width: 15px;
`;

const LegendLabel = styled.span`
  align-items: center;
  color: var(--grey-900);
  display: inline-flex;
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
`;

const OverviewMapContainer = styled.div`
  border-radius: 4px;
  box-shadow: var(--shadow-md);
  height: 420px;
  overflow: hidden;
  width: 100%;
`;

const TargetAreaSection = styled.div`
  margin-top: 24px;
`;

const SectionHeader = styled.h4`
  color: var(--grey-blue-400);
  font-family: var(--raleway);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 14px;
`;

const TargetAreaGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;
